import { useState, useMemo, useEffect } from 'react';

import { TextField, Button, MenuItem, Grid, Box } from '@mui/material';

import { PartnerDetails } from '@karehero/models';

import keni from '@/assets/loader/keni.svg';

import { TaskPageTemplate } from '@/components/templates/TaskPageTemplate/TaskPageTemplate';

export type PartnerRegisterProps = {
  topImage?: string;
  onSubmit?: (partner: Partial<PartnerDetails>) => void;
  validatePhone?: (phone?: string) => boolean;
  validatePartnerCode?: (code?: string) => boolean;
  requiresPartnerCode?: boolean;
  requiresLicenceCode?: boolean;
  firstName?: string;
  lastName?: string;
};

const LTC_LICENSE_OPTIONS = [
  { value: 'No licence or CF only', label: 'No Licence or CF only' },
  { value: 'SJP Diploma in LTC', label: 'SJP Diploma in LTC' },
  { value: 'SOLLA', label: 'SOLLA' },
];

type ValidatableField =
  'firstName' | 'lastName' | 'phoneNumber' | 'partnerCode' | 'ltcLicense';

export const PartnerRegister = ({
  topImage = keni,
  onSubmit,
  validatePhone = () => true,
  validatePartnerCode = () => true,
  requiresPartnerCode = true,
  requiresLicenceCode = true,
  firstName,
  lastName,
}: PartnerRegisterProps) => {
  const hasPrefilledName = !!firstName && !!lastName;

  const [formData, setFormData] = useState<Partial<PartnerDetails>>({
    firstName: firstName ?? '',
    lastName: lastName ?? '',
    phoneNumber: '',
    ...(requiresPartnerCode ? { partnerCode: '' } : {}),
    ...(requiresLicenceCode ? { ltcLicense: '' } : {}),
  });

  useEffect(() => {
    if (firstName) {
      setFormData((prev) => ({ ...prev, firstName }));
    }
    if (lastName) {
      setFormData((prev) => ({ ...prev, lastName }));
    }
  }, [firstName, lastName]);

  const [errors, setErrors] = useState<
    Partial<Record<ValidatableField, string>>
  >({});

  const validateField = (field: ValidatableField, value: string): string => {
    switch (field) {
      case 'firstName':
      case 'lastName':
        if (!value || value.trim().length < 2) {
          return 'Must be at least 2 characters';
        }
        if (!/^[a-zA-Z\s'-]+$/.test(value)) {
          return 'Invalid name format';
        }
        return '';
      case 'phoneNumber':
        if (!value) {
          return 'Phone number is required';
        }
        if (!/^[\d\s\-\+\(\)]+$/.test(value)) {
          return 'Invalid phone format';
        }
        if (!validatePhone(value)) {
          return 'Phone validation failed';
        }
        return '';
      case 'partnerCode':
        if (!requiresPartnerCode) return '';
        if (!value) {
          return 'Partner code is required';
        }
        if (!validatePartnerCode(value)) {
          return 'Invalid partner code';
        }
        return '';
      case 'ltcLicense':
        if (!requiresLicenceCode) return '';
        if (!value) {
          return 'LTC License is required';
        }
        return '';
      default:
        return '';
    }
  };

  const handleFieldChange =
    (field: ValidatableField) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    };

  const handleBlur = (field: ValidatableField) => () => {
    const error = validateField(
      field,
      (formData as Record<string, string>)[field] || '',
    );
    setErrors((prev) => ({ ...prev, [field]: error || undefined }));
  };

  const isValid = useMemo(() => {
    const hasBase =
      formData.firstName &&
      formData.lastName &&
      formData.phoneNumber &&
      !errors.firstName &&
      !errors.lastName &&
      !errors.phoneNumber;

    if (!hasBase) return false;

    if (requiresPartnerCode && (!formData.partnerCode || errors.partnerCode)) {
      return false;
    }
    if (requiresLicenceCode && (!formData.ltcLicense || errors.ltcLicense)) {
      return false;
    }

    return true;
  }, [formData, errors, requiresPartnerCode, requiresLicenceCode]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const fieldsToValidate: ValidatableField[] = [
      'firstName',
      'lastName',
      'phoneNumber',
    ];
    if (requiresPartnerCode) fieldsToValidate.push('partnerCode');
    if (requiresLicenceCode) fieldsToValidate.push('ltcLicense');

    const newErrors: Partial<Record<ValidatableField, string>> = {};
    fieldsToValidate.forEach((field) => {
      const error = validateField(
        field,
        (formData as Record<string, string>)[field] || '',
      );
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0 && isValid) {
      onSubmit?.(formData);
    }
  };

  const title = hasPrefilledName
    ? `Hi ${firstName}, finish setting up`
    : 'Finish setting up';

  return (
    <TaskPageTemplate
      title={title}
      description='A few more details so your clients can start using KareHero.'
      maxWidth='sm'
      topImage={topImage}
      form={
        <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Grid container spacing={2}>
              {!hasPrefilledName && (
                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label='First Name'
                      placeholder='Alan'
                      value={formData.firstName}
                      onChange={handleFieldChange('firstName')}
                      onBlur={handleBlur('firstName')}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label='Last Name'
                      placeholder='Bloggs'
                      value={formData.lastName}
                      onChange={handleFieldChange('lastName')}
                      onBlur={handleBlur('lastName')}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      required
                    />
                  </Grid>
                </>
              )}
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label='Phone Number'
                  placeholder='07123456789'
                  value={formData.phoneNumber}
                  onChange={handleFieldChange('phoneNumber')}
                  onBlur={handleBlur('phoneNumber')}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  required
                />
              </Grid>
              {requiresPartnerCode && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label='Partner Code'
                    placeholder='123456A'
                    value={formData.partnerCode}
                    onChange={handleFieldChange('partnerCode')}
                    onBlur={handleBlur('partnerCode')}
                    error={!!errors.partnerCode}
                    required
                  />
                </Grid>
              )}
              {requiresLicenceCode && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    select
                    label='Refer LTC License'
                    value={formData.ltcLicense}
                    onChange={handleFieldChange('ltcLicense')}
                    onBlur={handleBlur('ltcLicense')}
                    error={!!errors.ltcLicense}
                    helperText={errors.ltcLicense}
                    required
                  >
                    {LTC_LICENSE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
            </Grid>
            <Button
              type='submit'
              variant='contained'
              fullWidth
              disabled={!isValid}
              size='large'
            >
              Submit
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

PartnerRegister.displayName = 'PartnerRegister';
export default PartnerRegister;
